import { Dialog } from "@headlessui/react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/solid";
import { motion, AnimatePresence } from "framer-motion";
import { useRouter } from "next/router";
import { useContext, useEffect, useRef, useState } from "react";
import BrandMark from "~/components/BrandMark";
import ContactButtons from "~/components/ContactButtons";
import { Nav } from "~/components/Nav";
import { ViewportContext } from "~/providers/ViewportProvider";

const menuVariants = {
  closed: {
    x: "-100%",
    transition: {
      type: "spring" as const,
      stiffness: 400,
      damping: 40,
    },
  },
  open: {
    x: 0,
    transition: {
      type: "spring" as const,
      stiffness: 400,
      damping: 40,
    },
  },
};

const overlayVariants = {
  closed: { opacity: 0 },
  open: { opacity: 1 },
};

const Header: React.FC = () => {
  const { isMobile } = useContext(ViewportContext);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const router = useRouter();
  const mobileNavContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = document.getElementById("app");
    if (!container) return;
    const onScroll = () => setIsScrolled(container.scrollTop > 20);
    container.addEventListener("scroll", onScroll, { passive: true });
    return () => container.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const handleRouteChange = () => setIsMobileNavOpen(false);
    router.events.on("routeChangeComplete", handleRouteChange);
    return () => router.events.off("routeChangeComplete", handleRouteChange);
  }, [router.events]);

  useEffect(() => {
    if (!isMobileNavOpen) return;
    const handlePointerDown = (e: PointerEvent) => {
      if (
        mobileNavContainerRef.current &&
        !mobileNavContainerRef.current.contains(e.target as Node)
      ) {
        setIsMobileNavOpen(false);
      }
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [isMobileNavOpen]);

  return (
    <>
      <header
        style={{
          backgroundColor: isScrolled
            ? "rgba(250, 250, 250, 0.97)"
            : "rgba(250, 250, 250, 1)",
          boxShadow: isScrolled
            ? "0 10px 40px -10px rgb(0 0 0 / 0.15)"
            : "0 1px 3px 0 rgb(0 0 0 / 0.1)",
          paddingTop: isScrolled ? "8px" : "12px",
          paddingBottom: isScrolled ? "8px" : "12px",
          transition: "background-color 300ms, box-shadow 300ms, padding 300ms",
        }}
        className="sticky top-0 z-[120] flex w-full items-center justify-between gap-4 px-4 backdrop-blur-md sm:px-6 lg:px-8"
      >
        {isMobile ? (
          <div className="mx-2 flex w-full items-center justify-between sm:justify-start">
            <button
              onClick={() => setIsMobileNavOpen(true)}
              className="rounded-lg p-2 transition-colors hover:bg-gray-100 active:scale-95"
            >
              <span className="sr-only">Open navigation.</span>
              <Bars3Icon className="h-8 w-8 text-gray-700" />
            </button>
            <BrandMark compact />
            <ContactButtons isMobile={isMobile} />
          </div>
        ) : (
          <>
            <BrandMark />
            <Nav className="flex-wrap gap-x-1 gap-y-1" />
            <ContactButtons />
          </>
        )}
      </header>

      <AnimatePresence>
        {isMobileNavOpen && (
          <Dialog
            static
            as={motion.div}
            open={isMobileNavOpen}
            onClose={() => setIsMobileNavOpen(false)}
            className="fixed inset-0 z-[220]"
          >
            <motion.div
              variants={overlayVariants}
              initial="closed"
              animate="open"
              exit="closed"
              className="fixed inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setIsMobileNavOpen(false)}
            />

            <motion.div
              ref={mobileNavContainerRef}
              variants={menuVariants}
              initial="closed"
              animate="open"
              exit="closed"
              className="fixed inset-y-0 left-0 flex w-[92%] max-w-md flex-col bg-white shadow-2xl"
            >
              <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
                <BrandMark compact />
                <button
                  onClick={() => setIsMobileNavOpen(false)}
                  className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
                >
                  <span className="sr-only">Close navigation.</span>
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto py-4">
                <Nav className="flex-col" />
              </div>

              <div className="border-t border-gray-100 p-6">
                <div className="flex justify-center">
                  <BrandMark compact />
                </div>
              </div>
            </motion.div>
          </Dialog>
        )}
      </AnimatePresence>
    </>
  );
};

export { Header };
